import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMenuPermissions } from '../../contexts/MenuPermissionsContext';
import { flowFetch, FlowArea, FlowUser, formatDate } from './flowApi';
import { FlowDashboard } from './FlowDashboard';
import { FlowTaskList, TaskDetailModal } from './FlowTasks';
import { FlowAgenda } from './FlowAgenda';
import { FlowAreasList, FlowAreaDetail } from './FlowAreas';
import { FlowKanban } from './FlowKanban';
import { FlowChat } from './FlowChat';
import { FlowDocumentList } from './FlowDocuments';
import { FlowCommentsInbox } from './FlowComments';
import { FlowAdmin } from './FlowAdmin';
import { FlowPipeline } from './FlowPipeline';

const MENU = [
  { path: '/flow/inicio', label: 'Início', icon: 'M3 12l9-9 9 9M5 10v10h5v-6h4v6h5V10' },
  { path: '/flow/agenda', label: 'Minha Agenda', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { path: '/flow/tarefas', label: 'Minhas Tarefas', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { path: '/flow/areas', label: 'Áreas', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { path: '/flow/conversas', label: 'Conversas', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
  { path: '/flow/documentos', label: 'Documentos', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { path: '/flow/comentarios', label: 'Comentários', icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z' },
];

const TITLES: Record<string, string> = {
  '/flow/inicio': 'Início',
  '/flow/agenda': 'Minha Agenda',
  '/flow/tarefas': 'Minhas Tarefas',
  '/flow/areas': 'Áreas',
  '/flow/conversas': 'Conversas',
  '/flow/documentos': 'Documentos',
  '/flow/comentarios': 'Comentários',
  '/flow/pipeline': 'Pipeline',
  '/flow/admin': 'Administração',
};

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await flowFetch('/api/flow/notifications');
      setNotifications(data.notifications);
      setUnread(data.unread);
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    const handleClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => { clearInterval(interval); document.removeEventListener('mousedown', handleClick); };
  }, []);

  const markAllRead = async () => {
    await flowFetch('/api/flow/notifications/read', { method: 'POST', body: JSON.stringify({}) }).catch(() => {});
    load();
  };

  const openNotification = (n: any) => {
    setOpen(false);
    if (n.type === 'meeting') navigate('/flow/agenda');
    else if (n.channelId) navigate(`/flow/conversas?c=${n.channelId}`);
    else if (n.taskId) navigate(`/flow/tarefas?t=${n.taskId}${n.commentId ? `&c=${n.commentId}` : ''}`);
    else if (n.documentId) navigate(`/flow/documentos?d=${n.documentId}${n.commentId ? `&c=${n.commentId}` : ''}`);
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="relative rounded-full p-2 text-white/80 hover:bg-white/10">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-[1400] mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-slate-900">Notificações</p>
            {unread > 0 && <button onClick={markAllRead} className="text-xs text-cyan-600 hover:text-cyan-800">Marcar todas como lidas</button>}
          </div>
          <div className="max-h-96 space-y-1.5 overflow-y-auto">
            {notifications.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Sem notificações</p> :
              notifications.map(n => (
                <button key={n.id} onClick={() => openNotification(n)}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${n.read ? 'text-slate-500' : 'bg-cyan-50 font-medium text-slate-800'} ${(n.channelId || n.taskId || n.documentId) ? 'hover:bg-cyan-100' : ''}`}>
                  {n.channelId && <span className="mr-1">💬</span>}
                  {n.type === 'meeting' && <span className="mr-1">📅</span>}
                  {n.message}
                  <span className="block text-[11px] font-normal text-slate-400">{formatDate(n.createdAt)}</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GlobalSearch({ areas, users }: { areas: FlowArea[]; users: FlowUser[] }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<any>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setResults(null); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { setResults(await flowFetch(`/api/flow/search?q=${encodeURIComponent(q)}`)); } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div ref={ref} className="relative w-40 sm:w-64">
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar no Flow..."
        className="w-full rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm text-white placeholder-white/50 focus:bg-white focus:text-slate-900 focus:placeholder-slate-400 focus:outline-none" />
      {results && (
        <div className="absolute left-0 z-[1400] mt-2 max-h-96 w-96 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
          {['tasks', 'documents', 'areas', 'comments'].every(k => !results[k]?.length) && (
            <p className="py-4 text-center text-sm text-slate-400">Nada encontrado.</p>
          )}
          {results.tasks?.length > 0 && (
            <div className="mb-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">Tarefas</p>
              {results.tasks.map((t: any) => (
                <button key={t.id} onClick={() => { setDetailId(t.id); setResults(null); }}
                  className="block w-full rounded-lg px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-50">📋 {t.title}</button>
              ))}
            </div>
          )}
          {results.documents?.length > 0 && (
            <div className="mb-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">Documentos</p>
              {results.documents.map((d: any) => (
                <Link key={d.id} to="/flow/documentos" onClick={() => setResults(null)}
                  className="block rounded-lg px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-50">📄 {d.title}</Link>
              ))}
            </div>
          )}
          {results.areas?.length > 0 && (
            <div className="mb-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">Áreas</p>
              {results.areas.map((a: any) => (
                <Link key={a.id} to={`/flow/areas/${a.id}`} onClick={() => setResults(null)}
                  className="block rounded-lg px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-50">🗂️ {a.name}</Link>
              ))}
            </div>
          )}
          {results.comments?.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">Comentários</p>
              {results.comments.map((c: any) => (
                <button key={c.id} onClick={() => { if (c.taskId) setDetailId(c.taskId); setResults(null); }}
                  className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-sm text-slate-600 hover:bg-slate-50">💬 {c.content}</button>
              ))}
            </div>
          )}
        </div>
      )}
      {detailId && <TaskDetailModal taskId={detailId} areas={areas} users={users} onClose={() => setDetailId(null)} onChanged={() => {}} />}
    </div>
  );
}

export function FlowPage() {
  const location = useLocation();
  const { canSee } = useMenuPermissions();
  const visibleMenu = MENU.filter(item => canSee(`flow.${item.path.split('/')[2]}`));
  const [areas, setAreas] = useState<FlowArea[]>([]);
  const [users, setUsers] = useState<FlowUser[]>([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Admin do Flow vem do backend (papel NO TENANT ativo), não da role global
  const [isAdmin, setIsAdmin] = useState(false);

  // Fecha o menu mobile ao navegar
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    const loadUnread = async () => {
      try { const d = await flowFetch('/api/flow/chat/unread'); if (active) setChatUnread(d.unread || 0); } catch { /* ignore */ }
    };
    loadUnread();
    const interval = setInterval(loadUnread, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [location.pathname]);

  const loadBase = async () => {
    try {
      // archived=true é ignorado pelo backend para não-admins, então é seguro sempre pedir
      const [areasData, usersData] = await Promise.all([
        flowFetch('/api/flow/areas?archived=true'),
        flowFetch('/api/flow/users')
      ]);
      setIsAdmin(!!areasData.isAdmin);
      setAreas(areasData.areas);
      setUsers(usersData.users);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar o Flow');
    }
  };
  useEffect(() => { loadBase(); }, []);

  const fallbackPath = visibleMenu[0]?.path || '/flow/inicio';
  if (location.pathname === '/flow' || location.pathname === '/flow/') {
    return <Navigate to={fallbackPath} replace />;
  }

  const activeAreas = areas.filter(a => !a.archived);
  const isAreaDetail = /^\/flow\/areas\/.+/.test(location.pathname);
  const isBoardDetail = /^\/flow\/boards\/.+/.test(location.pathname);
  const title = (isAreaDetail || isBoardDetail) ? 'Áreas' : (TITLES[location.pathname] || 'Flow');

  const renderContent = () => {
    if (location.pathname === '/flow/inicio') return <FlowDashboard areas={activeAreas} users={users} />;
    if (location.pathname === '/flow/agenda') return <FlowAgenda areas={activeAreas} users={users} />;
    if (location.pathname === '/flow/tarefas') return <FlowTaskList areas={activeAreas} users={users} fixedFilter="mine" />;
    if (location.pathname === '/flow/areas') return <FlowAreasList areas={activeAreas} />;
    if (isBoardDetail) return <FlowKanban boardId={location.pathname.split('/')[3]} areas={activeAreas} users={users} isAdmin={isAdmin} />;
    if (isAreaDetail) return <FlowAreaDetail areaId={location.pathname.split('/')[3]} areas={activeAreas} users={users} isAdmin={isAdmin} />;
    if (location.pathname === '/flow/conversas') return <FlowChat users={users} />;
    if (location.pathname === '/flow/documentos') return <FlowDocumentList areas={activeAreas} users={users} />;
    if (location.pathname === '/flow/comentarios') return <FlowCommentsInbox areas={activeAreas} users={users} />;
    if (location.pathname === '/flow/pipeline') {
      if (!isAdmin) return <Navigate to="/flow/inicio" replace />;
      return <FlowPipeline areas={activeAreas} users={users} />;
    }
    if (location.pathname === '/flow/admin') {
      if (!isAdmin) return <Navigate to="/flow/inicio" replace />;
      return <FlowAdmin areas={areas} users={users} onAreasChanged={loadBase} />;
    }
    return <Navigate to="/flow/inicio" replace />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Overlay do drawer (mobile) */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 top-11 z-[1050] bg-black/40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar / Drawer */}
      <aside className={`fixed left-0 top-11 z-[1100] flex h-[calc(100vh-2.75rem)] h-[calc(100dvh-2.75rem)] w-60 max-w-[85vw] flex-col border-r border-sky-950/40 bg-[#0c4a6e] transition-transform duration-200 md:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/20 text-lg font-black text-cyan-200">F</div>
          <span className="text-sm font-bold text-white/90">Flow</span>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {visibleMenu.map(item => {
              const isActive = location.pathname === item.path || (item.path === '/flow/areas' && (isAreaDetail || isBoardDetail));
              return (
                <li key={item.path}>
                  <Link to={item.path}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${isActive ? 'bg-white/15 text-white shadow-sm' : 'text-white/60 hover:bg-white/10 hover:text-white/90'}`}>
                    <svg className={`h-5 w-5 ${isActive ? 'text-cyan-300' : 'text-white/50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                    <span className="flex-1">{item.label}</span>
                    {item.path === '/flow/conversas' && chatUnread > 0 && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-cyan-400 px-1.5 text-[10px] font-bold text-slate-900">{chatUnread}</span>
                    )}
                  </Link>
                </li>
              );
            })}
            {isAdmin && (
              <li>
                <Link to="/flow/pipeline"
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${location.pathname === '/flow/pipeline' ? 'bg-white/15 text-white shadow-sm' : 'text-white/60 hover:bg-white/10 hover:text-white/90'}`}>
                  <svg className={`h-5 w-5 ${location.pathname === '/flow/pipeline' ? 'text-cyan-300' : 'text-white/50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-8 4h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2zM13 7h.01" />
                  </svg>
                  <span className="flex-1">Pipeline</span>
                </Link>
              </li>
            )}
            {isAdmin && (
              <li>
                <Link to="/flow/admin"
                  className={`mt-2 flex items-center gap-3 rounded-lg border-t border-white/10 px-3 py-2.5 pt-4 text-sm font-medium transition ${location.pathname === '/flow/admin' ? 'text-white' : 'text-white/60 hover:text-white/90'}`}>
                  <svg className="h-5 w-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Administração
                </Link>
              </li>
            )}
          </ul>
        </nav>
        <div className="border-t border-white/10 px-5 py-3">
          <p className="text-[10px] text-white/30">Flow · IntegraIA</p>
        </div>
      </aside>

      {/* Conteúdo */}
      <main className="md:ml-60">
        <div className="sticky top-11 z-40 flex items-center justify-between gap-3 border-b border-slate-200 bg-[#0c4a6e] px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white/80 hover:bg-white/10 md:hidden"
              aria-label="Abrir menu"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="truncate text-lg font-black text-white">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <GlobalSearch areas={activeAreas} users={users} />
            <NotificationsBell />
          </div>
        </div>
        <div className="p-4 md:p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
